<template>
    <div class="OptionsContainer">
        <label
            v-if="label !== ''"
            class="OptionsContainer__label"
        >
            <template v-if="!isLoading">
                <span class="OptionsContainer__label-text">{{ label }}</span>
                <RPTooltip
                    v-if="labelTooltip !== ''"
                    :tooltip="labelTooltip"
                    class="TextInput__label-icon"
                />
            </template>
        </label>
        <ul class="OptionsContainer__options">
            <li
                v-if="selectAll"
                class="OptionsContainer__option"
            >
                <RPCheckbox
                    :value="isAllSelected"
                    :disabled="disabled"
                    label="Select All"
                    @input="selectAllOptions"
                />
            </li>
            <li
                v-for="opt in optionsFormatted"
                :key="opt.value"
                class="OptionsContainer__option"
            >
                <RPCheckbox
                    :value="isSelected(opt.value)"
                    :disabled="disabled && value.length >= limit"
                    :label="opt.label"
                    @input="select(opt.value)"
                />
            </li>
        </ul>
    </div>
</template>
<script>
import RPCheckbox from './RPCheckbox.vue';
import RPTooltip from 'components/Basic/Misc/RPTooltip.vue';
import stat from 'status/index';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'RPOptionsContainer',
    components: {
        RPCheckbox,
        RPTooltip
    },
    props: {
        label: {
            type: String,
            default: '',
        },
        labelTooltip: {
            type: String,
            default: '',
        },
        /**
         * @model
         */
        value: {
            type: Array,
            required: true,
        },
        limit: {
            type: Number,
            default: 999,
        },
        options: {
            type: Array,
            required: true,
        },
        selectAll: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        status: {
            type: String,
            default: stat.STANDBY
        }
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        optionsFormatted(){
            return this.options.map(opt => {
                return typeof opt === 'string'
                        || typeof opt === 'boolean'
                        || typeof opt === 'number'
                    ? {
                        value: opt,
                        label: String(opt),
                    }
                    : opt;
            });
        },
        isAllSelected(){
            return this.value.length === this.optionsFormatted.length;
        },
    },
    watch: {
        isAllSelected(newBool, oldBool){
            if(newBool !==oldBool){
                this.$emit('select-all', newBool);
            }
        }
    },
    methods: {
        isSelected(opt){
            return this.value.includes(opt);
        },
        select(opt){
            this.$emit('change', opt);

            let valArr = cloneDeep(this.value);
            if(this.isSelected(opt)) {
                valArr = valArr.filter(o => o !== opt);
            } else {
                valArr.push(opt);
            }

            this.$emit('input', valArr);
        },
        selectAllOptions(){
            const val = this.isAllSelected
                ? []
                : this.optionsFormatted.map(v => v.value);
            this.$emit('input', val);
        }
    }
};
</script>
<style lang="scss">
    .OptionsContainer {
        box-sizing: border-box;
        &__label {
            display: flex;
            font-size: .8rem;
            color: $grey;
            font-weight: 600;
            margin-bottom: .25rem;

            @include loading(.8rem, 7.5rem);

            &--disabled { color: $grey-200; }
            &-icon {
                margin: auto 0 auto .5rem;
            }
        }
        &__options {
            display: flex;
            flex-direction: column;
            padding: 0;
            margin: 0 0 0 0.25rem;
        }
        &__option {
            color: $text-grey;
            display: flex;
            cursor: pointer;
            align-items: center;
            &:not(:last-child){
                margin-bottom: 0.5rem;
            }
        }
    }
</style>

<docs>
Passing in a simple array of primitives:
```vue
<RPOptionsContainer
    label="Choose some options:"
    labelTooltip="More info"
    v-model="myValArray"
    :options="[
        1, 2, 3
    ]"
>
</RPOptionsContainer>
<br>
<pre>{{ {"v-model": myValArray} }}</pre>
```

Or an array of objects with `label` and `value` properties:
```vue
<RPOptionsContainer
    v-model="myValArray"
    :options="[
        {label: 'One', value: 1},
        {label: 'Two', value: 2},
        {label: 'Three', value: 3}
    ]"
>
</RPOptionsContainer>
<br>
<pre>{{ {"v-model": myValArray} }}</pre>
```

Select all options
```vue
<RPOptionsContainer
    v-model="myValArray"
    :options="[
        {label: 'One', value: 1},
        {label: 'Two', value: 2},
        {label: 'Three', value: 3}
    ]"
    select-all
>
</RPOptionsContainer>
<br>
<pre>{{ {"v-model": myValArray} }}</pre>
```
</docs>
